import React from "react";
import { AlertCircle, CalendarClock, CheckCircle2 } from "lucide-react";
import { AccountExpiry } from "../data/types";

/*
 * How long a club account has left, at a glance.
 *
 * The Association asked for warnings at 100, 50, 21, 7, 3, 2 and 1 days, so the
 * colour follows those marks rather than a smooth gradient: amber once inside a
 * warning band, red in the last week, and red for expired. A club with no
 * expiry set simply says so — an empty cell would read as a missing date.
 */
export default function ExpiryPill({ expiry, className = "" }: { expiry?: AccountExpiry | null; className?: string }) {
  if (!expiry || !expiry.expires) {
    return <span className={`text-[13px] text-muted ${className}`}>No expiry</span>;
  }

  if (expiry.expired) {
    return (
      <span className={`inline-flex items-center gap-1.5 rounded-md bg-danger-soft px-2 py-0.5 text-[12px] font-bold text-danger ${className}`}>
        <AlertCircle size={12} /> Expired {expiry.expiresAt}
      </span>
    );
  }

  const days = expiry.daysLeft ?? 0;
  // Inside a warning band the date matters; beyond it, it is just information.
  const tone =
    days <= 7 ? "bg-danger-soft text-danger" : expiry.warn !== null ? "bg-warning-soft text-warning" : "bg-slate-100 text-slate-600";
  const Icon = expiry.warn !== null ? AlertCircle : CheckCircle2;

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[12px] font-bold ${tone} ${className}`}>
      <Icon size={12} />
      {days === 0 ? "Expires today" : `${days} day${days === 1 ? "" : "s"} left`}
      <span className="font-semibold opacity-75">· {expiry.expiresAt}</span>
    </span>
  );
}

/*
 * The banner a club sees when its account is running out, or has run out.
 * Silent until a warning mark is reached — a notice shown every day for a year
 * stops being read.
 */
export function ExpiryBanner({ expiry, club, className = "" }: { expiry?: AccountExpiry | null; club?: string | null; className?: string }) {
  if (!expiry?.expires) return null;
  if (!expiry.expired && expiry.warn === null) return null;

  const expired = expiry.expired;
  return (
    <div
      className={`flex items-start gap-2.5 rounded-xl border px-4 py-3 ${
        expired ? "border-danger/40 bg-danger-soft" : "border-warning/40 bg-warning-soft"
      } ${className}`}
    >
      {expired ? <AlertCircle size={16} className="mt-0.5 shrink-0 text-danger" /> : <CalendarClock size={16} className="mt-0.5 shrink-0 text-warning" />}
      <div className="min-w-0 text-[13px] text-slate-700">
        <p className={`font-bold ${expired ? "text-danger" : "text-warning"}`}>
          {expired
            ? `The ${club || "club"} account expired on ${expiry.expiresAt}`
            : `The ${club || "club"} account expires in ${expiry.daysLeft} day${expiry.daysLeft === 1 ? "" : "s"}`}
        </p>
        <p className="mt-0.5">
          {expired
            ? "Uploading is paused. Everything already uploaded stays published and you can still view every result — contact the Association to renew."
            : `Renew before ${expiry.expiresAt} to keep uploading. Viewing is never affected.`}
        </p>
      </div>
    </div>
  );
}
